import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { UserService } from '../../../core/services/users.service';
import { debounceTime } from 'rxjs/operators';
import * as _ from 'lodash';
import { FuseConfigService } from '../../../core/services/config.service';
import { checkEmailOnServer, emailValidator } from '../../../core/validators/validators';
import {Subscription} from 'rxjs';

@Component({
    selector: 'profile-page',
    templateUrl: './profile.component.html',
    styleUrls: ['./profile.component.scss'],
    standalone: false
})
export class ProfileComponent implements OnInit, OnDestroy {
  public formGroup: FormGroup = new FormGroup({
    firstName: new FormControl('', Validators.required),
    lastName: new FormControl('', Validators.required),
    email: new FormControl({value: '', disabled: false}, [emailValidator , checkEmailOnServer]),
    lang: new FormControl('', Validators.required),
    eCall: new FormControl(''),
    phone: new FormControl('')
  });
  public subscription = new Subscription();
  public passwordForm: FormGroup = new FormGroup({
    oldPass: new FormControl(''),
    newPass: new FormControl('',
      Validators.compose([
        Validators.minLength(8),
        Validators.maxLength(32),
        Validators.pattern('^(?=.*?[a-z])(?=.*?[0-9])' +
          '[a-zA-Z0-9!@#$%^&*()+,-./:;<=>\u0027\\]\\[\\"\\\\?@^_`{|}~]{8,}$')
      ])),
    confirmPass: new FormControl('')
  });

  public notificationForm: FormGroup = new FormGroup({
    newRegistration: new FormControl(''),
    newBooking: new FormControl(''),
    id: new FormControl(''),
  });

  public passwordFormError = {
    oldPassError: true,
    newPassError: false,
    confirmPassError: false
  };
  public defaultUserData;
  public formWasChanged = false;
  public avatar = '';

  defNotifications = {
    newRegistration: true,
    newBooking: true,
    id: ''
  };

  roleId;
  public languages = [ {label: 'Germany' , value: 'de'},  {label: 'English' , value: 'en'} , {label: 'French' , value: 'fr'} ];

  constructor(private userService: UserService,
              private fuseConfig: FuseConfigService){
  }

  ngOnInit(){
    this.initForm();
  }

  initForm() {
    this.getUserInfo();

    this.subscription.add( this.passwordForm.controls.oldPass.valueChanges.pipe(debounceTime(1500)).subscribe(res => {
      if (res) {
        this.subscription.add(  this.userService.checkOldPassword(res).subscribe((check: any) => {
          if (!check.valid) {
            this.passwordFormError.oldPassError = true;
            this.passwordForm.controls.oldPass.setErrors({incorrect: true});
          } else {
            this.passwordFormError.oldPassError = false;
            this.passwordForm.controls.oldPass.setErrors(null);
          }
        }, error => {
          this.passwordFormError.oldPassError = true;
        }));
      }
    }));
    this.subscription.add( this.passwordForm.controls.newPass.valueChanges.subscribe(pass => {
      if (!this.passwordForm.controls.newPass.valid) {
        this.passwordFormError.newPassError = true;
      } else {
        this.passwordFormError.newPassError = false;
      }
    }));
    this.subscription.add( this.passwordForm.controls.confirmPass.valueChanges.subscribe(confirm => {
      if (confirm && confirm !== this.passwordForm.controls.newPass.value) {
        this.passwordFormError.confirmPassError = true;
        this.passwordForm.controls.confirmPass.setErrors({incorrect: true});
      } else {
        this.passwordFormError.confirmPassError = false;
        this.passwordForm.controls.confirmPass.setErrors(null);
      }
    }));

    this.subscription.add(  this.formGroup.valueChanges.subscribe(res => {
      if (!_.isEqual(res, this.defaultUserData) ||
        !_.isEqual(this.notificationForm.value, this.defNotifications)) {
        this.formWasChanged = true;
      } else {
        this.formWasChanged = false;
      }
    }));

    this.formGroup.controls.email.valueChanges.subscribe(res => {
      if (this.defaultUserData && this.defaultUserData.hasOwnProperty('email')) {
        const errors = this.formGroup.controls.email.errors;
        const emailWasChanged = res !== this.defaultUserData.email;
        if (!errors && emailWasChanged) {
          this.subscription.add(    this.userService.checkEmailValid(res).subscribe((data: any) => {
            if (!data.valid) {
              this.formGroup.controls.email.setErrors({checkEmailOnServer: true});
            }
          }));
        }
      }
    });

    this.notificationForm.valueChanges.subscribe(res => {
      if (!_.isEqual(res, this.defNotifications) ||
        !_.isEqual(this.formGroup.value, this.defaultUserData)) {
        this.formWasChanged = true;
      } else {
        this.formWasChanged = false;
      }
    });
  }

  getUserInfo(){
    this.subscription.add(  this.userService.getProfileData().subscribe((res: any) => {
      const userData: any = res;
      this.roleId = res.roleId;
      this.defaultUserData = {
        firstName: userData.firstName,
        lastName: userData.lastName,
        email: userData.email,
        lang: userData.lang,
        eCall: userData.eCall,
        phone: userData.phone
      };
      this.formGroup.patchValue({
        firstName: userData.firstName,
        lastName: userData.lastName,
        email: userData.email,
        lang: userData.lang,
        eCall: userData.eCall,
        phone: userData.phone
      });
      this.avatar = userData.logo;
      if (userData.notification && userData.notification.length) {
        this.defNotifications = {
          newRegistration: !!userData.notification[0].newRegistration,
          newBooking: !!userData.notification[0].newBooking,
          id: userData.notification[0].id
        };
      }
      this.notificationForm.patchValue(this.defNotifications);
    }));
  }

  changePassword() {
    this.subscription.add(  this.userService.updatePassword(this.defaultUserData.email, this.passwordForm.get('newPass').value)
      .subscribe(res => {
        this.passwordForm.reset();
      }));
  }

  saveProfile() {
    let data = this.formGroup.value;
    this.userService.updateProfile(data).subscribe((res: any) => {
      const userData: any = res;
      localStorage.setItem('name', (userData.firstName + ' ' + userData.lastName));
      this.fuseConfig.setUserName(localStorage.getItem('name'));
      localStorage.setItem('eCall', userData.eCall);
      this.fuseConfig.setECall(userData.eCall);
      localStorage.setItem('userEmail', userData.email);
      this.fuseConfig.setUserEmail(userData.email);
      this.formWasChanged = false;
      this.defaultUserData = {
        firstName: userData.firstName,
        lastName: userData.lastName,
        email: userData.email,
        lang: userData.lang,
        eCall: userData.eCall,
        phone: userData.phone
      };
      if (+res.roleId === 4) {
        let data: any = Object.assign({}, this.notificationForm.value);
        data.userId = res.id;
        if (!data?.id || data?.id === '') {
          delete data.id;
        }
        this.userService.updateNotification(data).subscribe((not: any) => {
          this.defNotifications = {
            newRegistration: not.newRegistration,
            newBooking: not.newBooking,
            id: not.id
          };
        });
      }
    });
  }
  ngOnDestroy() {
    this.subscription.unsubscribe();
  }
}
